import { z } from 'zod';
import { Provider, ProviderResultStatus, ScoringRequestStatus, UserRole } from '../enums.js';
import { newPasswordSchema, phoneSchema, tiyinSchema } from './common.js';
/**
 * Ma'muriyat uchun do'konlar tarixi.
 *
 * Savol oddiy: qaysi mijoz, qachon, qaysi do'konda, qaysi sotuvchi
 * orqali qanday limit oldi. Javob ham shunga qarab tuzilgan — bitta
 * qator = bitta scoring so'rovi, ichida ikkala provayder natijasi.
 *
 * Shaxsiy ma'lumot bu yerda MASKALANADI: tarix ko'rish uchun to'liq
 * raqam kerak emas, qidirish esa baribir ishlaydi (qidiruv bazadagi
 * qiymat bo'yicha ketadi).
 */
/** Ro'yxatdagi bitta provayder natijasi. */
export const historyProviderResultSchema = z.object({
    provider: z.enum([Provider.ALIF, Provider.UZUM]),
    status: z.nativeEnum(ProviderResultStatus),
    /** Tasdiqlangan summa, tiyinda. Rad etilgan bo'lsa — null. */
    maxAmount: tiyinSchema.nullable(),
    rejectReason: z.string().nullable(),
});
export const historyRowSchema = z.object({
    sessionId: z.string().uuid(),
    createdAt: z.string().datetime(),
    status: z.nativeEnum(ScoringRequestStatus),
    chosenProvider: z.enum([Provider.ALIF, Provider.UZUM]).nullable(),
    store: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    salesUser: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    client: z.object({
        id: z.string().uuid(),
        name: z.string(),
        /** 998*****1234 */
        phoneMask: z.string(),
    }),
    results: z.array(historyProviderResultSchema),
});
/** Sana faqat kun aniqligida keladi: `2026-08-24`. */
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
export const historyQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    storeId: z.string().uuid().optional(),
    salesUserId: z.string().uuid().optional(),
    provider: z.enum([Provider.ALIF, Provider.UZUM]).optional(),
    status: z.nativeEnum(ScoringRequestStatus).optional(),
    /** Shu kundan boshlab (o'zi ham kiradi). */
    from: daySchema.optional(),
    /** Shu kungacha (o'zi ham kiradi — kun oxirigacha olinadi). */
    to: daySchema.optional(),
    /** Mijoz ismi yoki telefon raqami. */
    q: z.string().trim().min(2).max(64).optional(),
});
export const historyResponseSchema = z.object({
    rows: z.array(historyRowSchema),
    /** Filtrga mos jami qatorlar — sahifalash uchun. */
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
});
/** Filtrdagi do'konlar ro'yxati. */
export const storeOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
/**
 * Foydalanuvchilar bo'limi (ADMIN).
 *
 * Sotuvchini yaratish, do'konga biriktirish, o'chirib qo'yish va
 * parolini almashtirish. Parol hech qachon qaytarilmaydi — faqat
 * o'rnatiladi.
 */
export const adminUserSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    phone: z.string(),
    role: z.nativeEnum(UserRole),
    isActive: z.boolean(),
    store: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    /** Alif sarlavhalarida ketadigan identifikator. */
    alifUserId: z.string().nullable(),
    /**
     * Bugun nechta MyID identifikatsiyasi qilingan.
     *
     * Har biri pul turadi — bu xarajatning ko'rinadigan tarafi.
     */
    identificationsToday: z.number().int().nonnegative(),
    lastLoginAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const createUserRequestSchema = z.object({
    name: z.string().trim().min(2, 'v.required').max(120),
    phone: phoneSchema,
    password: newPasswordSchema,
    role: z.nativeEnum(UserRole).default(UserRole.SALES),
    storeId: z.string().uuid().nullable().optional(),
    /**
     * Alifdagi sotuvchi raqami.
     *
     * Interfeysdan qo'yilmaydi: hozircha Alifda bittagina sotuvchi
     * ro'yxatdan o'tgan va hamma so'rov o'sha raqam bilan ketadi
     * (`.env` dagi zaxira). Maydon kelajak uchun qoldirilgan.
     */
    alifUserId: z.string().trim().max(64).nullable().optional(),
});
/**
 * Do'kon.
 *
 * Bu bizning hisobimiz uchun: qaysi sotuvchi qayerda ishlashini
 * ajratish. Provayderlarga yuborilmaydi — Alifga so'rov baribir
 * yagona ro'yxatdan o'tgan identifikator bilan ketadi.
 */
export const createStoreRequestSchema = z.object({
    name: z.string().trim().min(2, 'v.required').max(120),
    address: z.string().trim().max(240).nullable().optional(),
});
export const updateUserRequestSchema = z.object({
    name: z.string().trim().min(2, 'v.required').max(120).optional(),
    role: z.nativeEnum(UserRole).optional(),
    storeId: z.string().uuid().nullable().optional(),
    alifUserId: z.string().trim().max(64).nullable().optional(),
    isActive: z.boolean().optional(),
});
/** ADMIN boshqa foydalanuvchiga yangi parol qo'yadi. */
export const setPasswordRequestSchema = z.object({
    password: newPasswordSchema,
});
/**
 * O'z parolini almashtirish — joriy parol talab qilinadi.
 *
 * Sabab: ochiq qolgan kompyuterda begona odam parolni almashtirib,
 * hisobni o'zlashtirib olmasin.
 */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1, 'v.required'),
    newPassword: newPasswordSchema,
});
/**
 * Hodisalar tasmasi — «shu mijoz bilan nima bo'ldi».
 *
 * Manba — audit jurnali (bazada). Server loglari emas: ularga kirish
 * faqat bizda bor va har safar so'rab o'tirish kerak bo'ladi. Bu esa
 * ma'muriyat ekranida turadi.
 */
export const auditEventSchema = z.object({
    id: z.string().uuid(),
    at: z.string().datetime(),
    entity: z.string(),
    action: z.string(),
    /** Kim bajardi: sotuvchi nomi, `system` yoki provayder. */
    actorName: z.string().nullable(),
    /** Tafsilotlar — shaxsiy ma'lumot maskalangan holda. */
    payload: z.unknown().nullable(),
});
//# sourceMappingURL=admin.js.map